/**
 * Analytics events that make up a visitor's way through the CV funnel, read
 * back by /admin/journeys. Most steps are rows of their own (the interview, the
 * CV, its downloads, a guest checkout); these are the ones that otherwise leave
 * no trace: a click that failed, a limit that stopped someone, a checkout
 * opened and closed.
 *
 * Every event that belongs to a CV carries its `generation_id`, every chat
 * event its `chat_session_id` when there is one, so the admin can put them on
 * the right journey even for a visitor with tracking ids it has never seen.
 */
export const JOURNEY_EVENTS = {
  /** The homepage hero was shown (kept under the old A/B experiment's name). */
  heroView: 'ab_exposure',
  heroModeSwitch: 'hero_mode_switch',
  chatStarted: 'chat_started',
  chatReady: 'chat_ready',
  chatBuilt: 'chat_cv_built',
  chatRestart: 'chat_restart',
  /** The first question answered: an old CV to start from, or the interview. `{ choice: 'import' | 'interview' }` */
  chatOpening: 'chat_opening',
  /** An old CV or LinkedIn profile went into the chat. `{ how: 'file' | 'paste', chars }` */
  chatImport: 'chat_import',
  /** The uploaded file could not be read. `{ code }` */
  chatImportFailed: 'chat_import_failed',
  /** A daily or fair-use limit stopped the interview or the build. `{ step: 'message' | 'build' }` */
  chatLimit: 'chat_limit',
  /** The paste generator could not make a CV. `{ source, status }` */
  generateFailed: 'cv_generate_failed',
  /** A PDF download from the result. `{ generation_id, kind: 'free' | 'paid', template, ok, status? }` */
  download: 'cv_download',
  /** The free Basic download clicked: Basic free or a designed layout clean. `{ generation_id, action: 'shown' | 'free' | 'buy' }` */
  designUpsell: 'cv_design_upsell',
  /** A step of the path over the chat's result was clicked. `{ generation_id, step }` */
  resultPath: 'cv_result_path',
  /** A checkout opened from the result. `{ generation_id, plan, guest }` */
  checkoutOpen: 'cv_checkout_open',
  /** An address was given under the CV and the confirmation e-mail went out to it. `{ generation_id, gate, marketing }` */
  emailRequest: 'cv_email_request',
  /** The CV went towards an account. `{ generation_id, how: 'edit' | 'later' }` */
  save: 'cv_save',
  /** The CV was tailored to a job posting under the result. `{ generation_id, where, ok, score?, score_after?, changes?, seconds?, limited?, status? }` */
  jobMatch: 'cv_job_match',
  /** The first skill from the posting ticked as the visitor's own, once per tailoring. `{ generation_id, offered, score_after }` */
  jobMatchConfirm: 'cv_job_match_confirm',
  /** The first locked change added to the CV on the free preview, once per tailoring. `{ generation_id, offered, score_after }` */
  jobMatchAdd: 'cv_job_match_add',
  /** A line on where the visitor used a ticked skill was written or taken out; `lines` is how many there are now. `{ generation_id, lines }` */
  jobMatchSkillLine: 'cv_job_match_skill_line',
  /** The "now tailor it to the job" panel opened after a free download. `{ generation_id, tailored }` */
  afterDownloadOffer: 'cv_after_download_offer',
  /** A checkout opened from the result was paid. `{ generation_id, plan, guest }` */
  purchase: 'cv_purchase',
  /** The interview prep's preview was written for a posting. `{ generation_id, from: 'posting' | 'tailored' }` */
  interviewPrepPreview: 'interview_prep_preview',
  /** A chat CV's result was shown in its variant (chat-result experiment). `{ generation_id, source, result_experiment, result_variant }` */
  chatResultExposure: 'chat_result_exposure',
  /** Which layout a chat CV was first shown in (result-template experiment). `{ generation_id, experiment, variant, template }` */
  resultTemplate: 'result_template_exposure',
  /** A Civil Service advert was read, or reading it failed. `{ ok, status?, behaviours }` */
  csAdvertRead: 'cs_advert_read',
  /**
   * A step of the Civil Service tool reached for the first time on the page, for
   * its funnel on the analytics dashboard: 1 the advert pasted, 3 the CV, 4 the
   * questions (2 is the advert read, `cs_advert_read`). `{ step }`
   */
  csStep: 'cs_step',
  /** A Civil Service application was written for its preview, or writing it failed. `{ ok, status?, behaviours, statement }` */
  csDraft: 'cs_draft',
  /** An unpaid Civil Service application was saved for later, or saving it failed. `{ ok, via: 'email' | 'account', status? }` */
  csSaved: 'cs_saved',
  /** An NHS advert was read, or reading it failed. `{ ok, status?, criteria, format }` */
  nhsAdvertRead: 'nhs_advert_read',
  /** An NHS supporting statement was written for its preview, or writing it failed. `{ ok, status?, format, criteria, answered, asked }` */
  nhsDraft: 'nhs_draft',
  /**
   * The CV is in, in step 1 of the tailoring tool on /tailor-resume-to-job, or
   * reading it failed. `{ how: 'account' | 'guest' | 'paste' | 'file' | 'chat', ok, status? }`
   */
  tailorToolCv: 'tailor_tool_cv',
} as const

export type JourneyEventName = typeof JOURNEY_EVENTS[keyof typeof JOURNEY_EVENTS]
